import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { CATEGORIES, categoryIcon, categoryAccentStyle, categoryExample } from '../lib/categories';
import { VISIBILITY_OPTIONS } from '../lib/visibility';

const MAX_NAME = 100;
const MAX_REASON = 300;
// Visibility always starts at connections. It is never remembered between posts.
const EMPTY = { name: '', category: '', reason: '', visibility: 'connections' };

export const Add = () => {
  const navigate = useNavigate();
  const { recId } = useParams();
  const isEditing = Boolean(recId);
  const { isAuthenticated, user } = useAuth();
  const [formData, setFormData] = useState(EMPTY);
  const [initialData, setInitialData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [pendingHref, setPendingHref] = useState(null);
  const submittedRef = useRef(false);

  const dirty =
    !submittedRef.current &&
    (formData.name !== initialData.name ||
      formData.category !== initialData.category ||
      formData.reason !== initialData.reason ||
      formData.visibility !== initialData.visibility);

  useEffect(() => {
    if (!isEditing || !isAuthenticated) return;
    let active = true;
    (async () => {
      try {
        const rec = await api.getRecommendation(recId);
        if (!active) return;
        if (rec.authorId !== user?.id) {
          toast.error("That recommendation isn't available");
          navigate('/', { replace: true });
          return;
        }
        const loaded = {
          name: rec.name,
          category: rec.category,
          reason: rec.reason,
          visibility: rec.visibility === 'everyone' ? 'everyone' : 'connections',
        };
        setFormData(loaded);
        setInitialData(loaded);
      } catch (err) {
        toast.error("That recommendation isn't available");
        navigate('/', { replace: true });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [recId, isEditing, isAuthenticated, user?.id, navigate]);

  // Guard in-app navigation (the primary nav) and page unload while there is
  // unshared content. Browser back is not intercepted - see report.
  useEffect(() => {
    if (!dirty) return;
    const onClickCapture = (e) => {
      const link = e.target.closest?.('a[href]');
      if (!link || !link.closest('.bottom-nav')) return;
      const href = link.getAttribute('href');
      if (!href || href === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingHref(href);
    };
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    document.addEventListener('click', onClickCapture, true);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('click', onClickCapture, true);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [dirty]);

  const leave = (href) => {
    submittedRef.current = true;
    setPendingHref(null);
    navigate(href);
  };

  const handleClose = () => {
    if (dirty) setPendingHref('/');
    else navigate('/');
  };

  if (!isAuthenticated) {
    return (
      <div className="page" data-testid="add-page">
        <header className="page-header">
          <h1 className="page-title">Recommend something</h1>
        </header>
        <div className="placeholder">
          <h2 className="placeholder-title">Share your recommendations</h2>
          <p className="placeholder-text">
            Sign in to let people you trust know about the good things you&apos;ve found.
          </p>
          <button
            className="placeholder-btn"
            onClick={() => navigate('/signin', { state: { from: '/add', action: 'create' } })}
            data-testid="signin-to-add"
          >
            Sign in to continue
          </button>
        </div>
      </div>
    );
  }

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = 'Tell people what you are recommending';
    else if (formData.name.trim().length > MAX_NAME) next.name = `Keep this to ${MAX_NAME} characters or fewer`;
    if (!formData.category) next.category = 'Choose one category';
    if (!formData.reason.trim()) next.reason = 'Tell people why you recommend it';
    else if (formData.reason.trim().length > MAX_REASON) next.reason = `Keep this to ${MAX_REASON} characters or fewer`;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    const payload = {
      name: formData.name.trim(),
      category: formData.category,
      reason: formData.reason.trim(),
      visibility: formData.visibility,
    };
    try {
      if (isEditing) {
        await api.updateRecommendation(recId, payload);
        submittedRef.current = true;
        toast.success('Changes saved');
        navigate('/');
      } else {
        await api.createRecommendation(payload);
        submittedRef.current = true;
        toast.success('Recommended!', {
          description: formData.visibility === 'everyone'
            ? 'Your recommendation is now available to everyone on irecommend.'
            : 'Your recommendation is now available to people you trust.',
        });
        navigate('/');
      }
    } catch (err) {
      toast.error(isEditing ? 'Failed to save changes' : 'Failed to share your recommendation');
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  // Both examples always belong to the currently selected category.
  const example = categoryExample(formData.category);

  if (isEditing && loading) {
    return (
      <div className="page" data-testid="add-page">
        <div className="loading-state"><Loader2 className="loading-spinner" /></div>
      </div>
    );
  }

  return (
    <div className="page" data-testid={isEditing ? 'edit-page' : 'add-page'}>
      <header className="page-header recommend-header">
        <div className="recommend-header-top">
          <h1 className="page-title">{isEditing ? 'Edit recommendation' : 'Recommend something'}</h1>
          <button className="back-btn" onClick={handleClose} aria-label="Close" data-testid="recommend-close">
            <X />
          </button>
        </div>
        {!isEditing && (
          <p className="page-subtitle">
            What have you discovered that you&apos;d recommend to people you trust?
          </p>
        )}
      </header>

      <form onSubmit={handleSubmit} className="add-form" data-testid="add-form">
        <div className="form-group">
          <label className="form-label" htmlFor="name">What are you recommending?</label>
          <input
            id="name"
            type="text"
            className={`form-input ${errors.name ? 'form-input-error' : ''}`}
            placeholder={example.what}
            value={formData.name}
            maxLength={MAX_NAME}
            onChange={(e) => handleChange('name', e.target.value)}
            data-testid="input-name"
          />
          <span className="form-counter" data-testid="name-counter">
            {formData.name.length} / {MAX_NAME}
          </span>
          {errors.name && <span className="form-error" data-testid="error-name">{errors.name}</span>}
        </div>

        <div className="form-group">
          <span className="form-label" id="category-label">Category</span>
          <div
            className="chip-row wrap"
            role="radiogroup"
            aria-labelledby="category-label"
            data-testid="input-category"
          >
            {CATEGORIES.map((cat) => {
              const Icon = categoryIcon(cat);
              const active = formData.category === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`chip ${active ? 'active' : ''}`}
                  style={categoryAccentStyle(cat)}
                  onClick={() => handleChange('category', cat)}
                  data-testid={`category-chip-${cat}`}
                >
                  <Icon className="chip-icon" />
                  {cat}
                </button>
              );
            })}
          </div>
          {errors.category && <span className="form-error" data-testid="error-category">{errors.category}</span>}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="reason">Why do you recommend it?</label>
          <span className="form-hint">Tell people what makes it worth recommending.</span>
          <textarea
            id="reason"
            className={`form-textarea ${errors.reason ? 'form-input-error' : ''}`}
            placeholder={example.why}
            rows={5}
            value={formData.reason}
            maxLength={MAX_REASON}
            onChange={(e) => handleChange('reason', e.target.value)}
            data-testid="input-reason"
          />
          <span className="form-counter" data-testid="reason-counter">
            {formData.reason.length} / {MAX_REASON}
          </span>
          {errors.reason && <span className="form-error" data-testid="error-reason">{errors.reason}</span>}
        </div>

        <div className="form-group">
          <span className="form-label" id="visibility-label">Who can see this?</span>
          <div
            className="visibility-choice"
            role="radiogroup"
            aria-labelledby="visibility-label"
            data-testid="input-visibility"
          >
            {VISIBILITY_OPTIONS.map(({ value, label, hint, Icon }) => {
              const active = formData.visibility === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`visibility-option ${active ? 'active' : ''}`}
                  onClick={() => handleChange('visibility', value)}
                  data-testid={`visibility-option-${value}`}
                >
                  <Icon className="visibility-option-icon" />
                  <span className="visibility-option-text">
                    <span className="visibility-option-label">{label}</span>
                    <span className="visibility-option-hint">{hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <button type="submit" className="form-submit" disabled={submitting} data-testid="submit-btn">
          {submitting
            ? (isEditing ? 'Saving…' : 'Sharing…')
            : (isEditing ? 'Save changes' : 'Recommend')}
        </button>
      </form>

      {pendingHref && (
        <div className="limit-modal-overlay" data-testid="discard-confirm">
          <div className="limit-modal" role="dialog" aria-modal="true">
            <h3 className="limit-modal-title">
              {isEditing ? 'Discard your changes?' : 'Discard this recommendation?'}
            </h3>
            <p className="limit-modal-text">
              {isEditing
                ? "Your changes haven't been saved yet."
                : "Your recommendation hasn't been shared yet."}
            </p>
            <div className="limit-modal-actions">
              <button
                className="limit-modal-btn secondary"
                onClick={() => setPendingHref(null)}
                autoFocus
                data-testid="discard-cancel"
              >
                Keep editing
              </button>
              <button
                className="limit-modal-btn primary"
                onClick={() => leave(pendingHref)}
                data-testid="discard-confirm-btn"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
